import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { EmployeeBlocksEmployeeFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import PanesHeader from 'modules/core/components/PanesHeader'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import createAsyncData from 'modules/core/hooks/createAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import i18n from 'modules/core/lib/i18n'

import {
  useAddListToContractCuesContext,
  useHasContractCue,
} from 'modules/contracts/hooks/useHasContractCue'
import ContractCuesAlert from 'modules/contracts/pages/ContractVersionsPage/ContractCuesAlert'
import {
  FormTypes,
  useEmployeePersonalProvider,
} from 'modules/employees/components/blocks/EmployeePersonalProvider'
import { IdentifierTypes } from 'modules/employees/store/collections/identifierTypes'
import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
import getMaxLengthByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/fieldLength'
import getPlaceholderByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/placeholders'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import Identifiers from 'modules/payroll_employees/components/Identifiers'

import WorkPermit from './WorkPermit'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
  payroll: boolean
  isCurrent: boolean
}

const useFetch = createAsyncData((identifierTypes, country) =>
  identifierTypes.fetch({
    data: {
      country,
    },
  })
)

export const Identification = observer((props: Props) => {
  const { employee, payroll, readonly } = props
  const { chooseIdentityDocument, addIdNumber, addSocialSecurityNumber } =
    trackingEvents.employee.personal

  const identifierTypes = React.useRef(new IdentifierTypes()).current
  const loading = useFetch(identifierTypes, employee.legalEntity?.country)

  const options = identifierTypes.models.map(
    (item): SelectOption => ({
      value: item.attributes.slug,
      label: item.attributes.label,
    })
  )

  const { identificationForm: form, onSubmit: onSubmitIdentification } =
    useEmployeePersonalProvider()

  const [[submitEventHandler], setSubmitEventHandler] = React.useState<[() => void] | [null]>([
    null,
  ])

  const [countrySpecificIdentifiersDirty, setIdentifiersDirty] = React.useState(false)
  const maxDate = moment().clone().add(14, 'years')

  const onSubmit = () => {
    submitEventHandler?.()
    onSubmitIdentification([FormTypes.IDENTIFICATION])

    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(form),
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const fieldLabels: Record<string, string> = {
    identifier_type: i18n.t('employee_form.new_identifier_type'),
    identifier: i18n.t('employee_form.identifier'),
    social_security_number: i18n.t('employee_form.social_security_number'),
    identifier_expiration_date: i18n.t('employee_form.identifier_expiration_date'),
  }

  const isNie = form.get('identifier_type').value === 'nie'
  const hasSocialSecurity = form.has('country_metadata.social_security_number')

  const hasContractCue = useHasContractCue()
  // field name in incidenceRules differs from name in this form
  let listOfCuedFields = []
  if (
    hasSocialSecurity &&
    hasContractCue('social_security_number') &&
    !form.get('country_metadata.social_security_number').originalValue
  ) {
    listOfCuedFields.push('social_security_number')
  }

  listOfCuedFields = listOfCuedFields.concat(
    Object.keys(form.fields).filter((field) => {
      return hasContractCue(field) && !form.get(field).originalValue
    })
  )

  const translatedList = listOfCuedFields.map((field) => fieldLabels[field])
  useAddListToContractCuesContext('identification', translatedList)

  const countryEnumValue: Country = employee.legalEntity?.country as Country

  const getOptionSlug = options.find((x) => form.get('identifier_type').value === x.value)

  const identifier_type = options.some((x) => x.value === form.get('identifier_type').value)
    ? form.get('identifier_type').value
    : ''

  const showError: string = form.get('identifier').isDirty
    ? idValidationStrategies(
        countryEnumValue,
        identifier_type,
        form.get('identifier').value,
        getOptionSlug?.label || ''
      ).errors()
    : ''

  const showSsnError =
    form.has('country_metadata.social_security_number') &&
    form.get('country_metadata.social_security_number').isDirty
      ? idValidationStrategies(
          countryEnumValue,
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          'social_security_number' as SpainIdType,
          form.get('country_metadata.social_security_number').value,
          'SSN'
        ).errors()
      : ''

  const disabled = !!showError || !!showSsnError

  const showIdentifierTypeSelect = !loading && options.length > 0

  const idFieldsDisabled: boolean =
    (showIdentifierTypeSelect && form.values.identifier_type === '') || readonly

  const showSubmit = form.isDirty || countrySpecificIdentifiersDirty

  return (
    <div id="identification">
      <FormComponent onSubmit={onSubmit}>
        <FormRow multi>
          {showIdentifierTypeSelect && (
            <SelectField
              mandatory={hasContractCue('identifier_type')}
              label={fieldLabels.identifier_type}
              options={options}
              none={i18n.t('common.choose')}
              value={form.get('identifier_type').value}
              onChange={(option: string) => {
                form.setValues({ identifier_type: option })
              }}
              readonly={readonly}
              onBlur={() => track(chooseIdentityDocument)}
            />
          )}
        </FormRow>
        <FormRow>
          <TextField
            type={form.get('identifier_type').value === 'us_social_security' ? 'masked' : undefined}
            label={fieldLabels.identifier}
            value={form.get('identifier').value.toUpperCase()}
            onChange={(x) => form.setValues({ identifier: x })}
            error={showError}
            hideError
            mandatory
            onBlur={() => track(addIdNumber)}
            placeholder={
              countryEnumValue &&
              getPlaceholderByCountryAndIdType(countryEnumValue, identifier_type)
            }
            readonly={idFieldsDisabled}
            maxLength={
              countryEnumValue && getMaxLengthByCountryAndIdType(countryEnumValue, identifier_type)
            }
          />
          {showError && (
            <Box alignItems="flexStart">
              <Text size="200" color="radicalRed1000" capitalize>
                {showError}
              </Text>
            </Box>
          )}
        </FormRow>
        <FormRow>
          <DayPicker
            mandatory={false}
            label={fieldLabels.identifier_expiration_date}
            field={form.get('identifier_expiration_date')}
            readonly={idFieldsDisabled}
            maxDate={maxDate}
          />
        </FormRow>
        <FormRow>
          <Identifiers
            employeeId={employee.id}
            country={countryEnumValue}
            mandatory={payroll}
            readonly={readonly}
            setSubmitEventHandler={(onSubmitEvent: (() => void) | null) =>
              setSubmitEventHandler([onSubmitEvent])
            }
            setIdentifiersDirty={setIdentifiersDirty}
          />
        </FormRow>
        {hasSocialSecurity && (
          <>
            <FormRow>
              <TextField
                type="masked"
                avoidPasswordManagers
                mandatory={payroll || hasContractCue('social_security_number')}
                label={fieldLabels.social_security_number}
                value={form.get('country_metadata.social_security_number').value}
                onChange={(x) => form.setValues({ 'country_metadata.social_security_number': x })}
                placeholder={
                  countryEnumValue &&
                  getPlaceholderByCountryAndIdType(
                    countryEnumValue,
                    // eslint-disable-next-line no-type-assertion/no-type-assertion
                    'social_security_number' as SpainIdType
                  )
                }
                readonly={readonly}
                onBlur={() => track(addSocialSecurityNumber)}
                error={showSsnError}
                maxLength={
                  countryEnumValue &&
                  getMaxLengthByCountryAndIdType(
                    countryEnumValue,
                    // eslint-disable-next-line no-type-assertion/no-type-assertion
                    'social_security_number' as SpainIdType
                  )
                }
              />
            </FormRow>
            {isNie && (
              <FormRow>
                <WorkPermit field={form.get('country_metadata.has_work_permit')} />
              </FormRow>
            )}
          </>
        )}
        <ContractCuesAlert fields={translatedList}></ContractCuesAlert>
        {showSubmit && (
          <FormRow footer>
            <Button disabled={disabled} submit>
              {i18n.t('common.submit')}
            </Button>
          </FormRow>
        )}
      </FormComponent>
    </div>
  )
})

const IdentificationNumber = (props: Props) => {
  return (
    <PanesPadForm direction="horizontal" boxed>
      <PanesHeader
        illustration="id"
        title={i18n.t('employee_form.identification_number.new_title')}
        description={i18n.tCurrent(
          'employee_form.identification_number.new_description',
          props.isCurrent
        )}
      />
      <Identification {...props} />
    </PanesPadForm>
  )
}

export default observer(IdentificationNumber)
